import { Fragment, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  CaretDown,
  ChartBar,
  ChartLineUp,
  Star,
  ClipboardText,
  Flask,
  GraduationCap,
  List,
  ListChecks,
  Microphone,
  Path,
  SignOut,
  SquaresFour,
  Stack,
  UsersThree,
  X,
  type Icon,
} from '@phosphor-icons/react';
import { useAuth } from '../lib/auth';
import { ALL_COURSES, CourseProvider, courseHomePath, useCourse } from '../lib/course';
import { Avatar, cx, LoadingState } from './ui';

/** Course-scoped nav: `sub` is appended to the current course path; `match` runs
 *  against the pathname with the `/courses/:slug` prefix stripped. */
interface CourseNavItem {
  sub: string;
  label: string;
  icon: Icon;
  match: (subPath: string) => boolean;
}

// Student nav, grouped so it reads as Learn / Practise / Career rather than one
// long equal-weight list. (Interview Prep is shared across courses — rendered
// separately with that note.)
const COURSE_NAV_GROUPS: { heading: string; items: CourseNavItem[] }[] = [
  {
    heading: 'Learn',
    items: [
      // The course opens into its curriculum (the index). The class player is a
      // drill-in, so it keeps Curriculum lit; matches are exact/prefixed so the
      // picker at /courses doesn't light it.
      {
        sub: '',
        label: 'Curriculum',
        icon: Path,
        match: (s) => s === '' || s === '/course' || s.startsWith('/course/') || s.startsWith('/classes'),
      },
      { sub: 'progress', label: 'My Progress', icon: ChartLineUp, match: (s) => s === '/progress' },
    ],
  },
  {
    heading: 'Practise',
    items: [
      { sub: 'assessments', label: 'Assessments and Practice', icon: ListChecks, match: (s) => s.startsWith('/assessments') },
      { sub: 'labs', label: 'Projects & Labs', icon: Flask, match: (s) => s.startsWith('/labs') },
    ],
  },
];

interface NavItem {
  to: string;
  label: string;
  icon: Icon;
  match?: (path: string) => boolean;
}

// Instructor Studio, grouped so a dense menu is scannable: Insights / Build / People.
const STUDIO_NAV_GROUPS: { heading: string; items: NavItem[] }[] = [
  {
    heading: 'Insights',
    items: [
      { to: '/studio/overview', label: 'Overview', icon: ChartBar },
      { to: '/studio/reports', label: 'Reports', icon: ChartLineUp, match: (p) => p.startsWith('/studio/reports') },
      { to: '/studio/feedback', label: 'Feedback', icon: Star, match: (p) => p.startsWith('/studio/feedback') },
      { to: '/studio/grading', label: 'Grading queue', icon: ClipboardText },
    ],
  },
  {
    heading: 'Build',
    items: [
      { to: '/studio/courses', label: 'Courses', icon: GraduationCap },
      { to: '/studio/content', label: 'Content', icon: Stack, match: (p) => p.startsWith('/studio/content') },
      { to: '/studio/labs', label: 'Labs & Exercises', icon: Flask },
      { to: '/studio/interview', label: 'Interview Prep', icon: Microphone },
    ],
  },
  {
    heading: 'People',
    items: [{ to: '/studio/learners', label: 'Learners & Access', icon: UsersThree }],
  },
];

export function AppLayout() {
  return (
    <CourseProvider>
      <AppShell />
    </CourseProvider>
  );
}

/** '/' → your only course, your last course, or the course picker. */
export function CourseHome() {
  const { courses, loading } = useCourse();
  if (loading) return <LoadingState />;
  return <Navigate to={courseHomePath(courses)} replace />;
}

/** Layout for `/courses/:slug/*`: waits for courses, redirects unknown slugs to
 *  the picker, and remounts content when the course changes so it refetches. */
export function CourseLayout() {
  const { loading, currentSlug, currentCourse } = useCourse();
  if (loading) return <LoadingState />;
  if (currentSlug !== ALL_COURSES && !currentCourse) return <Navigate to="/courses" replace />;
  return (
    <div key={currentSlug ?? 'none'}>
      <Outlet />
    </div>
  );
}

function AppShell() {
  const { user, logout } = useAuth();
  const { courses, coursePath, currentCourse, currentSlug, goToCourse, courseProgress } = useCourse();
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const isInstructor = user?.role === 'instructor';
  const { pathname } = useLocation();
  const subPath = pathname.replace(/^\/courses\/[^/]+/, '');

  // Interview Prep is the career / job-prep track — relevant to the security course,
  // not the beginner AI-website course. Show it only when the learner has a non-AI
  // course (matched by slug and title so it's robust to the prod slug differing).
  const isAiCourse = (c: { slug: string; title: string }) =>
    ['ai-training', 'build-secure-ai-agent'].includes(c.slug) || /AI-Assisted/i.test(c.title);
  // Follow the course you're viewing: hidden while on the AI course (even for an
  // instructor previewing it). Off-course / all-courses → fall back to enrolment.
  const showInterview = currentCourse ? !isAiCourse(currentCourse) : courses.some((c) => !isAiCourse(c));

  const linkClass = ({ isActive }: { isActive: boolean }) => cx('nav-link', isActive && 'nav-link-active');

  const sidebar = (
    <div className="flex h-full flex-col gap-6 p-4">
      <div className="flex items-center gap-2.5 px-3 pt-2">
        <img src="/logo.png" alt="" className="h-9 w-9 shrink-0" />
        <div className="leading-tight">
          <div className="font-display text-[15px] font-extrabold tracking-wide text-brand-teal-bright">
            CYBER<span className="text-white">VAULT</span>
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">School</div>
        </div>
      </div>
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto">
        {courses.length >= 2 && (
          <NavLink
            to="/courses"
            onClick={() => setOpen(false)}
            className={cx('nav-link', pathname === '/courses' && 'nav-link-active')}
          >
            <SquaresFour size={20} />
            My Courses
          </NavLink>
        )}
        {/* Current-course context, above the nav so it's seen before choosing a page. */}
        {(currentCourse || currentSlug === ALL_COURSES) && (
          <div className="mb-2 mt-1 rounded-xl bg-white/5 p-3 text-xs">
            <div className="label-mono text-brand-teal-bright">
              {currentSlug === ALL_COURSES ? 'Instructor view' : isInstructor ? 'Viewing as learner' : 'Current course'}
            </div>
            {currentSlug === ALL_COURSES ? (
              <>
                <div className="mt-1 font-semibold text-white">All courses</div>
                <div className="mt-0.5 text-slate-400">Combined view across every course</div>
              </>
            ) : (
              <>
                <div className="mt-1 font-semibold leading-snug text-white">{currentCourse?.title}</div>
                <div className="mt-0.5 text-slate-400">
                  {currentCourse?.weeks ? `${currentCourse.weeks}-week course` : 'Course'}
                  {courseProgress != null ? ` · ${courseProgress}% complete` : ''}
                </div>
              </>
            )}
            {courses.length >= 2 && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  goToCourse(null);
                }}
                className="mt-2 font-semibold text-brand-teal-bright transition hover:underline"
              >
                Change course
              </button>
            )}
          </div>
        )}
        {COURSE_NAV_GROUPS.map((group) => (
          <Fragment key={group.heading}>
            <div className="mt-3 px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              {group.heading}
            </div>
            {group.items.map((item) => (
              <NavLink
                key={item.label}
                to={coursePath(item.sub)}
                onClick={() => setOpen(false)}
                className={cx('nav-link', item.match(subPath) && 'nav-link-active')}
              >
                <item.icon size={20} />
                {item.label}
              </NavLink>
            ))}
          </Fragment>
        ))}
        {showInterview && (
          <>
            <div className="mt-3 px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Career
            </div>
            <NavLink to="/interview" onClick={() => setOpen(false)} className={linkClass}>
              <Microphone size={20} />
              Interview Prep
            </NavLink>
            <p className="px-3 pt-0.5 text-[10px] leading-tight text-slate-500">Shared across your courses</p>
          </>
        )}
        {isInstructor && (
          <>
            <div className="mt-4 px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Instructor Studio
            </div>
            {STUDIO_NAV_GROUPS.map((group) => {
              // Insights stays open; Build/People collapse — but a group opens
              // automatically when it holds the page you're on, and you can toggle.
              const hasActive = group.items.some((it) => (it.match ? it.match(pathname) : pathname === it.to));
              const isOpen = openGroups[group.heading] ?? (group.heading === 'Insights' || hasActive);
              return (
                <Fragment key={group.heading}>
                  <button
                    type="button"
                    onClick={() => setOpenGroups((g) => ({ ...g, [group.heading]: !isOpen }))}
                    className="mt-2 flex w-full items-center justify-between px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 transition hover:text-slate-300"
                  >
                    {group.heading}
                    <CaretDown size={12} weight="bold" className={cx('transition', !isOpen && '-rotate-90')} />
                  </button>
                  {isOpen &&
                    group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className={item.match ? cx('nav-link', item.match(pathname) && 'nav-link-active') : linkClass}
                      >
                        <item.icon size={20} />
                        {item.label}
                      </NavLink>
                    ))}
                </Fragment>
              );
            })}
          </>
        )}
      </nav>
    </div>
  );

  return (
    <div data-shell className="flex h-screen overflow-hidden bg-brand-light">
      <aside className="hidden w-64 shrink-0 bg-brand-navy lg:block">{sidebar}</aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} aria-hidden="true" />
          <aside className="absolute left-0 top-0 h-full w-64 bg-brand-navy">{sidebar}</aside>
        </div>
      )}

      <div data-shell className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
          <button className="btn-ghost p-2 lg:hidden" onClick={() => setOpen((o) => !o)} aria-label="Toggle navigation">
            {open ? <X size={20} /> : <List size={20} />}
          </button>
          <span className="label-mono hidden sm:inline">Members area</span>
          <div className="flex items-center gap-3">
            {user && (
              <>
                <div className="hidden text-right sm:block">
                  <div className="text-sm font-semibold text-brand-navy">{user.name}</div>
                  <div className="text-xs capitalize text-slate-500">{user.role}</div>
                </div>
                <Avatar name={user.name} />
                <button className="btn-ghost p-2" onClick={() => logout()} aria-label="Sign out" title="Sign out">
                  <SignOut size={20} />
                </button>
              </>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
